/*
 *
 * ColorsPanel
 *
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import reducer from './reducer';
import { RootState } from './types';
import Panel from 'components/Panel';
import { makeSelectHaveFourDoors } from 'containers/Ribbon/ShelvesRibbon/selectors';
import {
  deleteSelectionAction,
  selectAllDoorsAction,
  selectAllPiecesAction,
  selectAllSidesAction,
} from 'containers/Ribbon/ShelvesRibbon/actions';
import PanesColorPicker from 'containers/PanesPanel/PanesColorPicker';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { useTranslation } from 'react-i18next';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import {
  makeSelectContext,
  makeSelectIsVisibleColorsPanel,
} from 'containers/Configurator/selectors/general';
import RemoveButton from 'components/RemoveButton';
import { makeSelectFullDrawersSelection } from 'containers/DrawersPanel/selectors';
import DoorsSelector from 'containers/PanesPanel/DoorsSelector';
import SidesSelector from 'containers/PanesPanel/SidesSelector';
import useMedia from 'use-media';
import AllSelector from 'components/AllSelector';
import styled from 'styled-components/macro';
// eslint-disable-next-line no-restricted-imports
import { css } from 'styled-components';
import PanelSwitcher from '../PanelSwitcher';
import { ActivePanel } from '../Configurator/constants/general';
import { FurnitureEditorContext } from '../../lib/constraintsChecker/FurnitureEditor';

interface StateProps {
  isVisible: boolean;
  lastHistoryCabinet: Cabinet;
  isFullDrawersSelection: boolean;
  hasFourDoors: boolean;
  context: FurnitureEditorContext;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  isVisible: makeSelectIsVisibleColorsPanel(ActivePanel.panes),
  lastHistoryCabinet: makeSelectLastHistoryCabinet(),
  isFullDrawersSelection: makeSelectFullDrawersSelection(),
  hasFourDoors: makeSelectHaveFourDoors(),
  context: makeSelectContext(),
});

function PanesPanel() {
  useInjectReducer({ key: 'colorsPanel', reducer: reducer });
  const dispatch = useDispatch();
  const {
    isVisible,
    isFullDrawersSelection,
    hasFourDoors,
    context,
  } = useSelector(stateSelector);
  const { t } = useTranslation();
  const isMobile = useMedia({ maxWidth: 1023 });
  const isXLarge = useMedia({ minWidth: 1280 });

  const hidePanel = () => {
    dispatch(setColorsPanelAction());
  };

  if (isMobile) {
    return null;
  }

  const showAllSelector =
    isXLarge && context !== FurnitureEditorContext.webShop;

  return (
    <Panel close={hidePanel} isVisible={isVisible} autoclose={false}>
      <div className="flex w-full">
        <div className="selection-items lg:w-1/2 flex justify-center">
          <div className="pieces-selection flex flex-col justify-center py-2 lg:py-0">
            {!hasFourDoors && (
              <div className="sethidden lg:block title text-base text-gray-900 self-center mb-2 ">
                <PanelSwitcher />
              </div>
            )}
            <div className="flex items-center py-0 ">
              <StyledSelectAll hasFourDoors={hasFourDoors}>
                {showAllSelector && (
                  <AllSelector
                    className="text-normal"
                    onClick={() => {
                      dispatch(selectAllPiecesAction());
                    }}
                  >
                    {t('All')}
                  </AllSelector>
                )}
              </StyledSelectAll>
              {showAllSelector && (
                <AllSelector
                  onClick={() => {
                    dispatch(selectAllSidesAction());
                  }}
                  className=""
                >
                  {t('Sides/Backpane')}
                </AllSelector>
              )}
              <SidesSelector />
            </div>
            {!isFullDrawersSelection && (
              <div className="flex justify-center items-center py-0">
                {showAllSelector && (
                  <AllSelector
                    onClick={() => {
                      dispatch(selectAllDoorsAction());
                    }}
                  >
                    {t('Doors')}
                  </AllSelector>
                )}
                <DoorsSelector />
              </div>
            )}
          </div>
          <div className=" ml-4 flex items-center">
            <RemoveButton
              onClick={() => {
                dispatch(deleteSelectionAction());
              }}
            />
          </div>
        </div>
        <div className="colors-picker lg:w-1/2">
          <div className="flex">
            <div className="mr-4">
              <PanesColorPicker paintMode={false} />
            </div>
          </div>
        </div>
      </div>
    </Panel>
  );
}

export default PanesPanel;

const StyledSelectAll = styled.div<{ hasFourDoors: boolean }>`
  display: none;
  @media (min-width: 1024px) {
    display: block;
    position: absolute;
    margin-top: -2rem;
    ${p =>
      p.hasFourDoors &&
      css`
        margin-top: 1.5rem;
        margin-left: 0.25rem;
      `}
  }
`;
